/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
  approveReportForClient as approveReportForClientService,
  findScalarOpinion as findScalarOpinionService,
  getScalarOpinion as getScalarOpinionService,
  toggleValuationVisibility as toggleValuationVisibilityService,
  updateScalarOpinion as updateScalarOpinionService,
} from 'dashboard409a/services/dashboard';
import { IFindValuationScalarOpinionResponse, IGetValuationScalarOpinionsResponse } from 'dashboard409a/services/types';
import {
  clearDashboardMeasurementDate,
  selectDashboardCompany,
  selectDashboardMeasurementDate,
} from 'dashboard409a/states/features/global';
import { IValuationState } from './types';

const initialState: IValuationState = {
  loading: false,
  allValuationScalarOpinion: null,
  currentValuationScalarOpinion: null,
  isValuationVisible: true,
  isPublicCompsVisible: true,
};

const syncLoadingState = (state: IValuationState) => {
  state.loading = Boolean(
    state.valuationListRequestId || state.currentOpinionRequestId || state.updateOpinionRequestId
  );
};

export const getValuationScalarOpinion = createAsyncThunk<IGetValuationScalarOpinionsResponse[], number>(
  'valuation/getValuationScalarOpinion',
  async (companyId, thunkAPI) => {
    try {
      return await getScalarOpinionService(companyId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const findValuationScalarOpinion = createAsyncThunk<IFindValuationScalarOpinionResponse, number>(
  'valuation/findValuationScalarOpinion',
  async (dateId, thunkAPI) => {
    try {
      return await findScalarOpinionService(dateId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateValuationScalarOpinion = createAsyncThunk<
  IFindValuationScalarOpinionResponse,
  { dateId: number; data: any }
>('valuation/updateValuationScalarOpinion', async ({ dateId, data }, thunkAPI) => {
  try {
    const response = await updateScalarOpinionService(dateId, data);
    return response as IFindValuationScalarOpinionResponse;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const toggleValuationVisibility = createAsyncThunk<
  { measurementDateId: number; visible: boolean },
  { measurementDateId: number; visible: boolean }
>('valuation/toggleValuationVisibility', async ({ measurementDateId, visible }, thunkAPI) => {
  try {
    await toggleValuationVisibilityService(measurementDateId, { is_valuation_visible: visible });
    enqueueSnackbar('Valuation visibility toggled', { variant: 'success' });
    return { measurementDateId, visible };
  } catch (error) {
    enqueueSnackbar('Failed to toggle valuation visibility. Please try again.', { variant: 'error' });
    return thunkAPI.rejectWithValue(error);  
  }
});

export const togglePublicCompsVisibility = createAsyncThunk<
  { measurementDateId: number; visible: boolean },
  { measurementDateId: number; visible: boolean }
>('valuation/togglePublicCompsVisibility', async ({ measurementDateId, visible }, thunkAPI) => {
  try {
    await toggleValuationVisibilityService(measurementDateId, { is_public_comps_visible: visible });
    enqueueSnackbar('Public comps visibility toggled', { variant: 'success' });
    return { measurementDateId, visible };
  } catch (error) {
    enqueueSnackbar('Failed to toggle public comps visibility. Please try again.', { variant: 'error' });
    return thunkAPI.rejectWithValue(error);
  }
});

export const approveReportForClient = createAsyncThunk<void, number>(
  'valuation/approveReportForClient',
  async (cmdId, thunkAPI) => {
    try {
      const response = await approveReportForClientService(cmdId);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const valuationSlice = createSlice({
  name: 'valuation',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(selectDashboardCompany, () => initialState);
    builder.addCase(selectDashboardMeasurementDate, (state, action) => {
      state.currentValuationScalarOpinion = null;
      state.currentOpinionRequestId = undefined;
      state.currentOpinionDateId = action.payload.currentCompanyMeasurementDateId;
      state.updateOpinionRequestId = undefined;
      state.isValuationVisible = true;
      state.isPublicCompsVisible = true;
      syncLoadingState(state);
    });
    builder.addCase(clearDashboardMeasurementDate, state => {
      state.currentValuationScalarOpinion = null;
      state.currentOpinionRequestId = undefined;
      state.currentOpinionDateId = undefined;
      state.updateOpinionRequestId = undefined;
      state.isValuationVisible = true;
      state.isPublicCompsVisible = true;
      syncLoadingState(state);
    });
    builder.addCase(getValuationScalarOpinion.pending, (state, action) => {
      state.allValuationScalarOpinion = null;
      state.valuationListRequestId = action.meta.requestId;
      syncLoadingState(state);
    });
    builder.addCase(getValuationScalarOpinion.fulfilled, (state, action) => {
      if (state.valuationListRequestId !== action.meta.requestId) {
        return;
      }

      state.allValuationScalarOpinion = action.payload;
      state.valuationListRequestId = undefined;
      syncLoadingState(state);
    });
    builder.addCase(getValuationScalarOpinion.rejected, (state, action) => {
      if (state.valuationListRequestId !== action.meta.requestId) {
        return;
      }

      state.valuationListRequestId = undefined;
      syncLoadingState(state);
    });
    builder.addCase(findValuationScalarOpinion.pending, (state, action) => {
      state.currentValuationScalarOpinion = null;
      state.currentOpinionRequestId = action.meta.requestId;
      state.currentOpinionDateId = action.meta.arg;
      syncLoadingState(state);
    });
    builder.addCase(findValuationScalarOpinion.fulfilled, (state, action) => {
      if (state.currentOpinionRequestId !== action.meta.requestId) {
        return;
      }

      state.currentValuationScalarOpinion = action.payload;
      state.currentOpinionRequestId = undefined;

      if (action.payload.is_valuation_visible !== undefined) {
        state.isValuationVisible = action.payload.is_valuation_visible;
      }

      if (action.payload.is_public_comps_visible !== undefined) {
        state.isPublicCompsVisible = action.payload.is_public_comps_visible;
      }

      syncLoadingState(state);
    });
    builder.addCase(findValuationScalarOpinion.rejected, (state, action) => {
      if (state.currentOpinionRequestId !== action.meta.requestId) {
        return;
      }

      state.currentValuationScalarOpinion = null;
      state.currentOpinionRequestId = undefined;
      syncLoadingState(state);
    });
    builder.addCase(updateValuationScalarOpinion.pending, (state, action) => {
      state.updateOpinionRequestId = action.meta.requestId;
      syncLoadingState(state);
    });
    builder.addCase(updateValuationScalarOpinion.fulfilled, (state, action) => {
      if (state.updateOpinionRequestId !== action.meta.requestId) {
        return;
      }

      state.updateOpinionRequestId = undefined;

      if (state.currentOpinionDateId === action.meta.arg.dateId) {
        state.currentValuationScalarOpinion = action.payload;

        if (action.payload.is_valuation_visible !== undefined) {
          state.isValuationVisible = action.payload.is_valuation_visible;
        }

        if (action.payload.is_public_comps_visible !== undefined) {
          state.isPublicCompsVisible = action.payload.is_public_comps_visible;
        }
      }

      syncLoadingState(state);
    });
    builder.addCase(updateValuationScalarOpinion.rejected, (state, action) => {
      if (state.updateOpinionRequestId !== action.meta.requestId) {
        return;
      }

      state.updateOpinionRequestId = undefined;
      syncLoadingState(state);
    });
    builder.addCase(toggleValuationVisibility.fulfilled, (state, action) => {
      if (state.currentOpinionDateId === action.payload.measurementDateId) {
        state.isValuationVisible = action.payload.visible;
      }
    });
    builder.addCase(togglePublicCompsVisibility.fulfilled, (state, action) => {
      if (state.currentOpinionDateId === action.payload.measurementDateId) {
        state.isPublicCompsVisible = action.payload.visible;
      }
    });
  },
});

export default valuationSlice.reducer;
